import React from "react";
import styled from "styled-components";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Popover from "@mui/material/Popover";

const Dropdown = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  id,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (option) => {
    onChange(option.value ?? option.label);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  return (
    <DropdownContainer disabled={disabled} id={id}>
      <DropdownHeader
        disabled={disabled}
        aria-owns={popoverId}
        aria-haspopup="true"
        onClick={handleClick}
        error={error}
      >
        <DropdownValue
          disabled={disabled}
          isPlaceholder={value === null || value === undefined}
        >
          {value ?? placeholder}
        </DropdownValue>
        <KeyboardArrowUpIconStyled disabled={disabled} active={open}>
          <KeyboardArrowUpIcon />
        </KeyboardArrowUpIconStyled>
      </DropdownHeader>
      {error && <ErrorText>{error}</ErrorText>}
      <Popover
        id="simple-popper"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        style={{
          borderRadius: "12px",
          overflow: "hidden !important",
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transitionDuration={0}
        PaperProps={{
          style: {
            width: `${anchorEl?.offsetWidth}px`,
            overflow: "hidden",
            borderRadius: "12px",
            background: "transparent !important",
            boxShadow: "none !important",
          },
        }}
      >
        <DropdownSelectorContainer width={anchorEl?.offsetWidth}>
          {options.map((option, index) => (
            <DropdownOption
              isFirst={index === 0}
              key={option.value}
              onClick={() => handleOptionClick(option)}
            >
              <RadioButton
                isChecked={value === (option?.value ?? option?.label)}
              />
              <OptionLabel>{option.label}</OptionLabel>
            </DropdownOption>
          ))}
        </DropdownSelectorContainer>
      </Popover>
    </DropdownContainer>
  );
};

export default Dropdown;

export const KeyboardArrowUpIconStyled = styled.div`
  transform: ${({ active }) => (active ? "rotate(0deg)" : "rotate(180deg)")};
  transition: all 200ms;
  height: max-content;
  display: grid;
  place-items: center;

  color: ${({ disabled }) => (disabled ? "#A0A0A0" : "black")};
`;

export const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background-color: ${({ disabled }) => (disabled ? "#f8f8f8" : "#ffffff")};
  border: 1px solid ${({ error }) => (error ? "#c54600" : "#ddd")};
  border-radius: 12px;
`;

export const DropdownValue = styled.div`
  font-size: 16px !important;
  line-height: 20px;
  color: ${({ isPlaceholder, disabled }) =>
    isPlaceholder || disabled ? "#A0A0A0" : "black"};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
`;

export const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
  cursor: pointer;
  max-width: 100%;
  min-width: 0;
  width: 100%;

  ${({ disabled }) => disabled && "cursor: not-allowed;"}
`;

export const DropdownSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 12px;
  overflow-y: auto;
  border: 1px solid #ddd;
  max-height: calc(53px * 5);
  width: 100%;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.12);
`;

export const DropdownOption = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e8e8e8;
  cursor: pointer;

  &:first-child {
    border-top: none;
  }
`;

export const ErrorText = styled.div`
  font-size: 14px !important;
  color: #c54600;
`;

export const RadioButton = styled.div`
  min-width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid black;
  position: relative;

  &:after {
    content: "";
    width: 10px;
    height: 10px;
    background-color: black;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${(props) => (props.isChecked ? "block" : "none")};
  }
`;

export const OptionLabel = styled.div`
  font-size: 16px !important;
  line-height: 20px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
